import { Grid } from '@design-system';
import { isUpcoming } from '@utils/product';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import React from 'react';

import { useIsSubscribed } from '../../../hooks/subscription/useIsSubscribed';
import {
  GeneratedGetDrawByProductIDsQuery,
  GeneratedSubscriptionType,
} from '../../../types/generated';
import {
  AssetStoryblok,
  PlannedReleaseStoryblok,
} from '../../../types/generated-storyblok';
import PageSection from '../../Layout/PageSection';
import ProductTile from '../../ProductTile';
import { ProductTilePlaceholder } from '../../ProductTile/ProductTilePlaceholder';

interface Props {
  upcoming?: PlannedReleaseStoryblok[];
  products?: MultiCurrencyProduct[];
  draws: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'];

  // Planned release
  artist?: string;
  image?: AssetStoryblok;
  isSubscribed: boolean;
  isLoading: boolean;
  onSubscribe: () => Promise<void>;

  analytics: Analytics;
}

export const ArtistProductsTab: React.FC<Props> = ({
  analytics,
  artist,
  draws,
  image,
  isLoading,
  isSubscribed,
  onSubscribe,
  products,
  upcoming,
}) => {
  const upcomingProducts = products?.filter((product) => {
    return isUpcoming(product);
  });

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    upcomingProducts?.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })) || [],
  );

  return (
    <div className="flex flex-col gap-9 md:gap-12">
      <PageSection>
        <div className="mx-auto w-full max-w-3xl lg:max-w-5xl">
          <Grid className="gap-y-6" columns={2}>
            {upcoming?.map((item) => (
              <ProductTilePlaceholder
                key={item._uid}
                artist={artist}
                image={image}
                isLoading={isLoading}
                isSubscribed={isSubscribed}
                onSubscribe={onSubscribe}
                title={item.title}
              />
            ))}

            {products?.map((item, i) => {
              const draw = draws?.find((d) => {
                return d.productId === shopifyGraphqlIdToAdminId(item.id);
              });

              return (
                <ProductTile
                  key={item.id}
                  analytics={{
                    ...analytics,
                    position: i,
                  }}
                  draw={draw}
                  isSubscribed={checkIsSubscribed(
                    shopifyGraphqlIdToAdminId(item.id),
                    GeneratedSubscriptionType.Product,
                  )}
                  onSubscribe={() => {
                    refetch();
                  }}
                  product={item}
                />
              );
            })}
          </Grid>
        </div>
      </PageSection>
    </div>
  );
};
